
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

const ProtectedRoute: React.FC<{ children: JSX.Element }> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const authProcessingComplete = useRef(false);

  useEffect(() => {
    if (!user && !isLoading) {
      console.log("Usuário não autenticado, tentando redirecionar para login", {
        path: location.pathname,
        fullUrl: window.location.href,
        hasHash: window.location.hash ? true : false,
        hashLength: window.location.hash.length
      });
    }
  }, [user, isLoading, location.pathname]);

  // Processamento especial para fragmentos de autenticação (access_token no hash)
  useEffect(() => {
    const processAuthHash = async () => {
      if (window.location.hash && window.location.hash.includes('access_token') && !authProcessingComplete.current) {
        authProcessingComplete.current = true;
        console.log("Detectado hash de autenticação no ProtectedRoute, processando...");
        
        try {
          // Tentar processar o hash de autenticação diretamente
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error("Erro ao processar hash de autenticação:", error);
          } else if (data.session) {
            console.log("Sessão obtida com sucesso a partir do hash");
            
            // Limpar o hash da URL sem recarregar a página
            if (window.history && window.history.replaceState) {
              window.history.replaceState(
                null,
                document.title,
                window.location.pathname + window.location.search
              );
            }
          }
        } catch (error) {
          console.error("Erro ao processar hash de autenticação:", error);
        }
      }
    };
    
    processAuthHash();
    
    // Reset the flag when the component unmounts
    return () => {
      authProcessingComplete.current = false;
    };
  }, [location.pathname]);

  // Verifica se estamos no meio de um fluxo de autenticação
  // E verifica especificamente se o hash contém um access_token
  const isAuthenticating = window.location.hash && window.location.hash.includes('access_token');
  
  if (isLoading || isAuthenticating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="loader"></div>
          <p className="text-sm text-muted-foreground">
            {isAuthenticating ? "Completando autenticação..." : "Carregando..."}
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Usar state para lembrar onde o usuário estava tentando acessar
    console.log("Redirecionando para a página inicial a partir de:", location.pathname);
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  return children;
};

export default ProtectedRoute;
